import React, { useMemo, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { MultiplierComboModal } from './effects/MultiplierComboModal';
import { RpgCriticalWinBanner } from './effects/RpgCriticalWinBanner';

export function ResultModal({
  open = true,
  variant = 'jackpot', // 'jackpot', 'multiplier', or 'critical'
  title,
  multiplier = '.00',
  amount,
  streak = 3,
  subtitle,
  subtitle2,
  onClose,
  portalContainerRef,
  portalContainer,
}) {
  const displayMultiplier = useMemo(() => {
    if (!multiplier) return '1.5X';
    const raw = String(multiplier).trim();
    const numMatch = raw.match(/(\d+(?:\.\d+)?)/);
    if (/multiplier/i.test(raw) && numMatch) {
      return `${numMatch[1]}X`;
    }
    if (/^\d+(\.\d+)?$/i.test(raw)) {
      return `${raw}X`;
    }
    if (/^\d+(\.\d+)?x$/i.test(raw)) {
      return raw.toUpperCase();
    }
    return raw;
  }, [multiplier]);

  const [tilt, setTilt] = useState({ x: 0, y: 0 });
  const [isInteracting, setIsInteracting] = useState(false);

  const handlePointerMove = useCallback((e) => {
    const card = e.currentTarget;
    const rect = card.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    if (clientX === undefined || clientY === undefined) return;
    const x = clientX - rect.left;
    const y = clientY - rect.top;
    const centerX = rect.width / 2;
    const centerY = rect.height / 2;
    const rotateX = Math.max(-10, Math.min(10, ((centerY - y) / centerY) * 10));
    const rotateY = Math.max(-10, Math.min(10, ((x - centerX) / centerX) * 10));
    setTilt({ x: rotateX, y: rotateY });
  }, []);

  const handlePointerLeave = useCallback(() => {
    setTilt({ x: 0, y: 0 });
    setIsInteracting(false);
  }, []);

  const handlePointerEnter = useCallback(() => {
    setIsInteracting(true);
  }, []);

  if (!open) return null;

  const body = typeof document !== 'undefined' ? document.body : null;
  const target = portalContainerRef?.current ?? portalContainer ?? body;
  if (!target) return null;

  const isScopedToLayout = Boolean(body && target !== body);
  const isMultiplier = variant === 'multiplier';
  const isCritical = variant === 'critical' || variant === 'critical-win';

  if (isMultiplier) {
    return createPortal(
      <MultiplierComboModal
        onClose={onClose}
        multiplier={displayMultiplier}
      />,
      target
    );
  }

  if (isCritical) {
    return createPortal(
      <RpgCriticalWinBanner
        onClose={onClose}
        title={title || 'CRITICAL WIN'}
        amount={amount || multiplier}
        multiplier={displayMultiplier}
        streak={streak}
      />,
      target
    );
  }

  return createPortal(
    <div
      className={`${isScopedToLayout ? 'absolute' : 'fixed'} inset-0 z-[9999] backdrop-blur-md px-4`}
      style={{ background: 'rgba(0,0,0,0.85)', display: 'grid', placeItems: 'center' }}
    >
      <style>{`
        @keyframes floatCoin {
          0%, 100% { transform: translateY(0) rotate(0deg); }
          50% { transform: translateY(-10px) rotate(5deg); }
        }
        @keyframes floatCoinReverse {
          0%, 100% { transform: translateY(0) rotate(0deg); }
          50% { transform: translateY(10px) rotate(-5deg); }
        }
        .animate-float {
          animation: floatCoin 4s ease-in-out infinite;
        }
        .animate-float-reverse {
          animation: floatCoinReverse 3.5s ease-in-out infinite;
        }
        .jackpot-amount-text {
          background: linear-gradient(180deg, #FDF0A6 0%, #D89F2C 50%, #FDF0A6 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          filter: drop-shadow(0px 4px 2px rgba(0,0,0,0.8)) drop-shadow(0px 0px 10px rgba(216,159,44,0.6));
        }
      `}</style>

      <div
        className="relative flex items-center justify-center select-none"
        style={{
          width: 'min(360px, 90vw)',
          aspectRatio: '393 / 585',
        }}
      >
        {/* Inner Solid Background */}
        <div 
          className="absolute inset-[4%] rounded-[5%] shadow-[inset_0_0_40px_rgba(0,0,0,0.8)] pointer-events-none" 
          style={{ background: 'radial-gradient(circle at center, #6b1d14 0%, #2a0502 70%)' }}
        />
        {/* Main Background Board */}
        <img 
          src="/assets/result-modal/1 1.png" 
          alt="board" 
          className="absolute inset-0 w-full h-full object-contain pointer-events-none z-10"
          draggable="false"
        />
        {/* Floating Coins */}
        <img src="/assets/result-modal/6 1.png" alt="coin" className="absolute w-[22%] -left-[6%] top-[15%] animate-float pointer-events-none z-30" draggable="false" />
        <img src="/assets/result-modal/6 3 (1).png" alt="coin" className="absolute w-[15%] -right-[8%] top-[30%] animate-float-reverse pointer-events-none z-30" draggable="false" />
        <img src="/assets/result-modal/6 3.png" alt="coin" className="absolute w-[15%] left-[8%] bottom-[12%] animate-float-reverse pointer-events-none z-30" draggable="false" />
        <img src="/assets/result-modal/6 4.png" alt="coin" className="absolute w-[11%] right-[12%] bottom-[25%] animate-float pointer-events-none z-30" draggable="false" />
        {/* Banner with Text */}
        <div 
          className="absolute w-[80%] top-[8%] flex items-center justify-center pointer-events-none z-20"
          style={{ aspectRatio: '253/108', left: 0, right: 0, margin: '0 auto' }}
        >
          <img 
            src="/assets/result-modal/3 1.png" 
            alt="banner bg" 
            className="absolute inset-0 w-full h-full object-contain"
            draggable="false"
          />
          <div 
            className="relative z-10 text-center font-black leading-[1.1] tracking-wide"
            style={{ 
              fontSize: 'min(1.4rem, 6vw)',
              color: '#FCE78D',
              textShadow: '0 2px 2px rgba(0,0,0,0.8), 0 0 10px rgba(252,231,141,0.5)'
            }}
          >
            {title || (
              <>JACKPOT<br/>HIT!</>
            )}
          </div>
        </div>
        {/* Amount Text */}
        <div 
          className="absolute top-[32%] left-0 w-full text-center font-black italic tracking-tighter jackpot-amount-text z-20"
          style={{ fontSize: 'min(3.8rem, 16vw)' }}
        >
          {multiplier}
        </div>
        {/* Subtitle 1 */}
        <div className="absolute top-[48%] left-[10%] right-[10%] text-center z-20">
          <p className="text-white font-bold text-[15px] leading-snug drop-shadow-md">
            {subtitle || 'Congratulations!'}
          </p>
        </div>
        {/* Divider with Star */}
        <div className="absolute top-[59%] left-[15%] right-[15%] flex items-center justify-center gap-2">
          <div className="h-[2px] flex-1 bg-gradient-to-r from-transparent via-[#FDE047] to-[#D97706]" />
          <img src="/assets/result-modal/4 1.png" alt="star" className="w-[18px] h-[18px] object-contain drop-shadow-lg shrink-0 pointer-events-none" draggable="false" />
          <div className="h-[2px] flex-1 bg-gradient-to-l from-transparent via-[#FDE047] to-[#D97706]" />
        </div>
        {/* Subtitle 2 */}
        <div className="absolute top-[66%] left-[12%] right-[12%] text-center z-20">
          <p className="text-[#FDE047] font-bold text-[14px] leading-snug drop-shadow-md">
            {subtitle2 || 'You claimed a portion of the Jackpot Pool.'}
          </p>
        </div>
        {/* Next Button */}
        <button
          className="absolute bottom-[8%] w-[60%] aspect-[177/55] active:scale-95 transition-transform flex items-center justify-center focus:outline-none z-20 bg-transparent border-none p-0"
          style={{ left: 0, right: 0, margin: '0 auto', cursor: 'pointer' }}
          onClick={onClose}
        >
          <img 
            src="/assets/result-modal/1233123 - Copy 1.png" 
            alt="Next Button Bg" 
            className="absolute inset-0 w-full h-full object-contain pointer-events-none"
            draggable="false"
          />
          <span 
            className="relative z-10 text-white font-black text-2xl tracking-wide"
            style={{
              textShadow: '0px 3px 0px #047857, 0px 4px 4px rgba(0,0,0,0.5)',
              WebkitTextStroke: '1px #064E3B'
            }}
          >
            Next
          </span>
        </button>
      </div>
    </div>,
    target
  );
}
